import {
  ref,
  onMounted,
  onUnmounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { IlsctlLineItem } from "@/models/IlsctlLineItem";

import { truckLoadReportsController } from "@/controllers/truckLoadReports";
import { JSConn } from "@/utilities/js-conn";
import {
  TruckLoadReport,
  LineItemSummary,
  TruckLoadInventoryLotSummary,
} from "@/models/TruckLoadReport";

import { trace } from "@/utilities/development";
import { TruckLoadLineItem } from "@/models/TruckLoadLineItem";
import { itemRouteBuilder, NamedRts } from "@/router";
import { currencyFormatter } from "@/utilities/display-helpers";

import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";
import { format, subDays } from "date-fns";

export default defineComponent({
  name: "truckLoadReport",
  components: {
    LoadingAnimation,
  },
  setup(_props, ctx) {
    const $store = ctx.root.$store;

    const dataLoaded = ref(false);
    const loadingMessage = ref(
      "Please wait while the inventory lots are loaded...",
    );

    const showDateForm = ref(true);

    const firstDate = ref("");
    const lastDate = ref("");

    let { submitButtonText, submitting } = buildSubmitButton();

    const apiTruckLoadReport = computed(() => {
      return $store.getters.truckLoadReport as TruckLoadReport;
    });

    const ilsctlLineItems = ref<IlsctlLineItem[]>([]);

    const truckLoadLineItems = ref<TruckLoadLineItem[]>([]);

    const truckLoadLineItemSummaries = ref<LineItemSummary[]>([]);

    const ilSummaries = ref<TruckLoadInventoryLotSummary[]>([]);
    const scSummaries = ref<TruckLoadInventoryLotSummary[]>([]);

    const apiIlsctlLineItems = computed(() => {
      return apiTruckLoadReport.value.ilsctl_line_items;
    });

    const apiTruckLoadLineItems = computed(() => {
      return apiTruckLoadReport.value.truck_load_line_items;
    });

    const apiTruckLoadLineItemSummaries = computed(() => {
      return apiTruckLoadReport.value.line_item_summaries;
    });

    const apiTruckLoadInventoryLotSummaries = computed(() => {
      return apiTruckLoadReport.value.il_summaries;
    });

    const apiTruckLoadSellContractSummaries = computed(() => {
      return apiTruckLoadReport.value.sc_summaries;
    });

    const tlShowRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.buildTruckLoad, id);
    };

    const scShowRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.sellContractIlsctlLineItems, id);
    };

    const ilShowRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.inventoryLotIlsctlLineItems, id);
    };

    const onSubmit = (evt: any) => {
      evt.preventDefault();

      const jsConn = new JSConn();
      submitButtonText.value = "Submitting...";
      submitting.value = true;

      jsConn.formParams = {
        first_date: firstDate.value,
        last_date: lastDate.value,
      };

      loadData(jsConn).then(() => {
        submitButtonText.value = "Submit";
        submitting.value = false;
      });
    };

    onMounted(() => {
      const jsConn = new JSConn();

      const td = new Date();

      firstDate.value = format(subDays(td, 14), "yyyy-MM-dd");
      lastDate.value = format(td, "yyyy-MM-dd");

      let truckLoadIds: string[] = [];

      if (ctx.root.$route.query?.truckLoadIds) {
        truckLoadIds = ctx.root.$route.query.truckLoadIds as string[];
      }

      if (truckLoadIds.length > 0) {
        showDateForm.value = false;
        jsConn.formParams = {
          truck_load_ids: truckLoadIds,
        };
      } else {
        // get some start and stop dates
        jsConn.formParams = {
          first_date: firstDate.value,
          last_date: lastDate.value,
        };
      }

      loadData(jsConn);
    });

    const loadData = (jsConn: JSConn) => {
      return Promise.all([
        truckLoadReportsController.createTruckLoadReport(jsConn),
      ]).then(response => {
        ilsctlLineItems.value = [...apiIlsctlLineItems.value];
        truckLoadLineItems.value = [...apiTruckLoadLineItems.value];
        truckLoadLineItemSummaries.value = [
          ...apiTruckLoadLineItemSummaries.value,
        ];
        ilSummaries.value = [...apiTruckLoadInventoryLotSummaries.value];
        scSummaries.value = [...apiTruckLoadSellContractSummaries.value];
        dataLoaded.value = true;
        return response;
      });
    };

    const cff = (n: number) => {
      return currencyFormatter.format(n);
    };

    return {
      dataLoaded,
      loadingMessage,

      tlShowRouteBuilder,
      scShowRouteBuilder,
      ilShowRouteBuilder,

      apiTruckLoadReport,
      ilsctlLineItems,
      truckLoadLineItems,
      truckLoadLineItemSummaries,
      ilSummaries,
      scSummaries,

      currencyFormatter,

      firstDate,
      lastDate,

      showDateForm,
      submitButtonText,
      submitting,
      onSubmit,

      cff,
    };
  },
});
